import { FC, useCallback, useEffect, useState } from 'react';

import {
  DashboardRole,
  getDashboardProfitAnalysis,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  ProfitAnalysisResponse,
  ProfitTypeSlice,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';
import { formatPrice } from '@shared/utils/formatPrice';

import { fmtPeriod, fmtRate } from '../utils/dashboard.utils';
import { CenterLoading, EmptyBox, ErrorBox, StatCard } from './DashboardShared';

type SliceTab = 'all' | 'coupon' | 'delivery';

const EMPTY_SLICE: ProfitTypeSlice = {
  totalCount: 0,
  convertedCount: 0,
  windfallCount: 0,
  conversionRate: 0,
  windfallRate: 0,
  revenue: 0,
  convertedRevenue: 0,
  windfallRevenue: 0,
  serviceFee: 0,
  cogs: 0,
  convertedProfit: 0,
  convertedProfitRate: 0,
  windfallProfit: 0,
  windfallProfitRate: 0,
  totalProfit: 0,
  totalProfitRate: 0,
};

function safeSlice(src?: ProfitTypeSlice): ProfitTypeSlice {
  if (!src) return EMPTY_SLICE;
  const raw = src as unknown as Record<string, number>;
  return Object.fromEntries(
    Object.entries(EMPTY_SLICE).map(([k, def]) => [k, raw[k] ?? def]),
  ) as unknown as ProfitTypeSlice;
}

function summaryToSlice(s: ProfitAnalysisResponse['summary']): ProfitTypeSlice {
  return {
    totalCount: s.totalItemCount ?? 0,
    convertedCount: s.convertedCount ?? 0,
    windfallCount: s.windfallCount ?? 0,
    conversionRate: s.conversionRate ?? 0,
    windfallRate: s.windfallRate ?? 0,
    revenue: s.totalRevenue ?? 0,
    convertedRevenue: s.totalConvertedRevenue ?? 0,
    windfallRevenue: s.totalWindfallRevenue ?? 0,
    serviceFee: s.totalServiceFee ?? 0,
    cogs: s.totalCogs ?? 0,
    convertedProfit: s.totalConvertedProfit ?? 0,
    convertedProfitRate: s.convertedProfitRate ?? 0,
    windfallProfit: s.totalWindfallProfit ?? 0,
    windfallProfitRate: s.windfallProfitRate ?? 0,
    totalProfit: s.totalProfit ?? 0,
    totalProfitRate: s.totalProfitRate ?? 0,
  };
}

export const DashboardProfitTab: FC<{
  from: string;
  to: string;
  role?: DashboardRole;
}> = ({ from, to, role }) => {
  const [data, setData] = useState<ProfitAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sliceTab, setSliceTab] = useState<SliceTab>('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getDashboardProfitAnalysis(from, to, role));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '데이터를 불러올 수 없습니다.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <CenterLoading />;
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!data) return <EmptyBox />;

  const s = data.summary;
  const activeSlice: ProfitTypeSlice =
    sliceTab === 'coupon'
      ? safeSlice(s.coupon)
      : sliceTab === 'delivery'
      ? safeSlice(s.delivery)
      : summaryToSlice(s);

  return (
    <div className="space-y-6">
      {/* 헤더 + 유형 탭 */}
      <section className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              수익 분석
              <span className="ml-2 text-xs font-normal text-gray-400">
                결제일 기준 ({fmtPeriod(from, to)})
              </span>
            </h2>
            <p className="mt-1 text-xs text-gray-400">
              상품별로 받은 건(전환)과 안 받은 건(낙전)을 나눠서 수익을 분석합니다.
            </p>
          </div>
          <div className="flex rounded-md bg-gray-200 p-0.5 text-xs font-semibold">
            {(['all', 'coupon', 'delivery'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setSliceTab(t)}
                className={`rounded px-3 py-1 transition-colors ${
                  sliceTab === t
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'all' ? '전체' : t === 'coupon' ? '쿠폰' : '배송'}
              </button>
            ))}
          </div>
        </div>

        {/* 개요 카드 */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label="총 매출"
            value={`${formatPrice(activeSlice.revenue)}원`}
            sub={
              sliceTab === 'all'
                ? `${s.orderCount}건 주문`
                : `${activeSlice.totalCount}건`
            }
            accent="indigo"
          />
          <StatCard
            label="수수료"
            value={`${formatPrice(activeSlice.serviceFee)}원`}
            accent="red"
          />
          <StatCard
            label="매입 원가"
            value={`${formatPrice(activeSlice.cogs)}원`}
            sub={`${activeSlice.convertedCount}건 전환`}
            accent="orange"
          />
          <StatCard
            label="총 수익"
            value={`${formatPrice(activeSlice.totalProfit)}원`}
            sub={`수익률 ${fmtRate(activeSlice.totalProfitRate)}`}
            accent="emerald"
          />
        </div>

        {/* 전환·낙전 분석 */}
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <HighlightCard
            label="전환 수익"
            value={activeSlice.convertedProfit}
            rate={activeSlice.convertedProfitRate}
            count={activeSlice.convertedCount}
            total={activeSlice.totalCount}
            revenue={activeSlice.convertedRevenue}
            revenueLabel="전환 매출"
            ratioLabel="전환율"
            ratio={activeSlice.conversionRate}
            color="emerald"
          />
          <HighlightCard
            label="낙전 수익"
            value={activeSlice.windfallProfit}
            rate={activeSlice.windfallProfitRate}
            count={activeSlice.windfallCount}
            total={activeSlice.totalCount}
            revenue={activeSlice.windfallRevenue}
            revenueLabel="낙전 매출"
            ratioLabel="낙전율"
            ratio={activeSlice.windfallRate}
            color="amber"
          />
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-medium text-gray-500">건수 분포</p>
            <div className="mt-2 space-y-1.5">
              <CountBar
                label="전환"
                count={activeSlice.convertedCount}
                total={activeSlice.totalCount}
                color="bg-emerald-500"
              />
              <CountBar
                label="낙전"
                count={activeSlice.windfallCount}
                total={activeSlice.totalCount}
                color="bg-amber-400"
              />
            </div>
            <p className="mt-2 text-right text-xs text-gray-400">
              전체 {activeSlice.totalCount}건
            </p>
          </div>
        </div>
      </section>

      {/* 교차 검증 */}
      {data.crossCheck && (
        <section className="rounded-lg bg-white p-6 shadow">
          <h2 className="mb-3 text-lg font-bold text-gray-800">금액 검증</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              label="결제 금액 합계"
              value={`${formatPrice(data.crossCheck.byPayment)}원`}
            />
            <StatCard
              label="상품 판매가 합계"
              value={`${formatPrice(data.crossCheck.byItemRevenue)}원`}
            />
            <StatCard
              label="차이"
              value={`${formatPrice(data.crossCheck.diff)}원`}
              accent={data.crossCheck.matched ? 'emerald' : 'red'}
            />
            <div className="flex items-center justify-center rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
              <span
                className={`rounded-full px-3 py-1 text-sm font-bold ${
                  data.crossCheck.matched
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {data.crossCheck.matched ? '일치' : '불일치'}
              </span>
            </div>
          </div>
          {!data.crossCheck.matched && (
            <p className="mt-3 text-xs text-red-500">
              결제 금액과 상품 판매가 합계가 일치하지 않습니다. 부분 취소 등으로 차이가
              발생할 수 있습니다.
            </p>
          )}
        </section>
      )}
    </div>
  );
};

/* ── 하위 컴포넌트 ── */

const HighlightCard: FC<{
  label: string;
  value: number;
  rate: number;
  count: number;
  total: number;
  revenue: number;
  revenueLabel: string;
  ratioLabel: string;
  ratio: number;
  color: 'emerald' | 'amber';
}> = ({
  label,
  value,
  rate,
  count,
  total,
  revenue,
  revenueLabel,
  ratioLabel,
  ratio,
  color,
}) => {
  const styles = {
    emerald: {
      border: 'border-emerald-200',
      bg: 'bg-emerald-50',
      label: 'text-emerald-600',
      value: 'text-emerald-700',
      sub: 'text-emerald-500',
      countBg: 'bg-emerald-100',
      countText: 'text-emerald-700',
    },
    amber: {
      border: 'border-amber-200',
      bg: 'bg-amber-50',
      label: 'text-amber-600',
      value: 'text-amber-700',
      sub: 'text-amber-500',
      countBg: 'bg-amber-100',
      countText: 'text-amber-700',
    },
  }[color];

  return (
    <div className={`rounded-lg border ${styles.border} ${styles.bg} px-4 py-4`}>
      <div className="flex items-center justify-between">
        <p className={`text-xs font-medium ${styles.label}`}>{label}</p>
        <span
          className={`rounded-full ${styles.countBg} px-2.5 py-0.5 text-sm font-bold ${styles.countText}`}
        >
          {count}/{total}건
        </span>
      </div>
      <p className={`mt-2 text-2xl font-bold ${styles.value}`}>{formatPrice(value)}원</p>
      <p className="mt-1 text-sm text-gray-500">
        {revenueLabel}{' '}
        <span className="font-semibold text-gray-700">{formatPrice(revenue)}원</span> 대비
      </p>
      <div className={`mt-2 flex items-center gap-3 text-xs ${styles.sub}`}>
        <span>수익률 {fmtRate(rate)}</span>
        <span className="text-gray-300">|</span>
        <span>
          {ratioLabel} {fmtRate(ratio)}
        </span>
      </div>
    </div>
  );
};

const CountBar: FC<{
  label: string;
  count: number;
  total: number;
  color: string;
}> = ({ label, count, total, color }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-8 text-gray-500">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-14 text-right font-medium text-gray-600">
        {count}건 <span className="text-gray-400">({pct.toFixed(0)}%)</span>
      </span>
    </div>
  );
};
